/**
 * Game Constants
 *
 * Stores immutable game-related constants such as monster definitions, board dimensions,
 * game phases, and other fixed values.
 *
 * Integration:
 * - Used by: Various game components, hooks, and utility functions.
 * - Provides: A single source of truth for static game data.
 */

import type { MonsterDefinition, MonsterTypeId, MonsterName, MovementTuple } from '../types/monster.types';

// ============================================================================
// Board Constants
// ============================================================================

/**
 * Board dimensions
 */
export const BOARD_ROWS = 20;
export const BOARD_COLS = 10;

/**
 * Player halves (for island placement validation)
 * Player A: rows 0-9 (squares 0-99)
 * Player B: rows 10-19 (squares 100-199)
 */
export const PLAYER_A_MAX_SQUARE = 99;
export const PLAYER_B_MIN_SQUARE = 100;

// ============================================================================
// Monster Constants
// ============================================================================

/**
 * Number of monsters per player
 */
export const MONSTERS_PER_PLAYER = 5;

/**
 * Monster definitions with movement patterns
 * Matches backend/src/game/monsters.ts
 * Movement patterns: [deltaX, deltaY] relative to current position
 * Note: Y-axis is inverted (negative Y = up, positive Y = down)
 */
export const MONSTER_DEFINITIONS: Record<MonsterTypeId, MonsterDefinition> = {
    1: {
        id: 1,
        name: 'Leviathan' as MonsterName,
        movement: [
            [0, -3], [-1, -2], [0, -2], [1, -2], [0, -1],
            [-1, 0], [1, 0], [0, 1],
            [-1, 2], [0, 2], [1, 2], [0, 3]
        ] as MovementTuple[],
    },
    2: {
        id: 2,
        name: 'Sea Hydra' as MonsterName,
        movement: [
            [-2, -2], [2, -2],
            [-1, -1], [1, -1],
            [-1, 1], [1, 1],
            [-2, 2], [2, 2]
        ] as MovementTuple[],
    },
    3: {
        id: 3,
        name: 'Kraken' as MonsterName,
        movement: [
            [-1, -2], [1, -2],
            [-2, -1], [-1, -1], [1, -1], [2, -1],
            [-2, 1], [-1, 1], [1, 1], [2, 1],
            [-1, 2], [1, 2]
        ] as MovementTuple[],
    },
    4: {
        id: 4,
        name: 'White Whale' as MonsterName,
        movement: [
            [0, -1],
            [-1, 0], [1, 0],
            [-1, 1], [0, 1], [1, 1],
            [-1, 2], [0, 2], [1, 2],
            [-1, 3], [0, 3], [1, 3]
        ] as MovementTuple[],
    },
    5: {
        id: 5,
        name: 'Megalodon' as MonsterName,
        movement: [
            [-1, -1], [0, -1], [1, -1],
            [-2, 1], [-1, 1], [0, 1], [1, 1], [2, 1],
            [-2, 2], [-1, 2], [0, 2], [1, 2], [2, 2]
        ] as MovementTuple[],
    },
    6: {
        id: 6,
        name: 'Siren' as MonsterName,
        movement: [
            [0, -1],
            [-1, 0], [1, 0],
            [0, 1],
            [0, 2],
            [0, 3],
            [0, 4]
        ] as MovementTuple[],
    },
    7: {
        id: 7,
        name: 'Giant Squid' as MonsterName,
        movement: [
            [0, -3], [0, -2], [0, -1],
            [-3, 0], [-2, 0], [-1, 0], [1, 0], [2, 0], [3, 0],
            [0, 1], [0, 2], [0, 3]
        ] as MovementTuple[],
    },
    8: {
        id: 8,
        name: 'Ebirah' as MonsterName,
        movement: [
            [-1, -3], [0, -3], [1, -3],
            [-1, -2], [0, -2], [1, -2],
            [0, -1],
            [-1, 1], [1, 1],
            [-1, 2], [1, 2]
        ] as MovementTuple[],
    },
    9: {
        id: 9,
        name: 'Jormungandr' as MonsterName,
        movement: [
            [-3, 1], [-2, 2], [-1, 3],
            [0, 2],
            [1, 1],
            [-1, -1],
            [0, -2],
            [1, -3], [2, -2], [3, -1]
        ] as MovementTuple[],
    },
    10: {
        id: 10,
        name: 'Loch Ness Monster' as MonsterName,
        movement: [
            [0, -2],
            [-4, 0], [-2, 0], [2, 0], [4, 0],
            [0, 2], [0, 4]
        ] as MovementTuple[],
    },
};

/**
 * Monster list - array format for compatibility with existing code
 * Used for UI selection lists and dropdowns
 */
export const monsterList: Array<{ id: number; name: string; url: string }> = [
    { id: 1, name: 'Leviathan', url: 'leviathan' },
    { id: 2, name: 'Sea Hydra', url: 'sea_hydra' },
    { id: 3, name: 'Kraken', url: 'kraken' },
    { id: 4, name: 'White Whale', url: 'white_whale' },
    { id: 5, name: 'Megalodon', url: 'megalodon' },
    { id: 6, name: 'Siren', url: 'siren' },
    { id: 7, name: 'Giant Squid', url: 'giant_squid' },
    { id: 8, name: 'Ebirah', url: 'ebirah' },
    { id: 9, name: 'Jormungandr', url: 'jormungandr' },
    { id: 10, name: 'Loch Ness Monster', url: 'loch_ness' },
];

// ============================================================================
// Game Phase Constants
// ============================================================================

/**
 * Game phase numeric mapping (for backward compatibility)
 */
export const GAME_PHASE = {
    LOBBY: 0,
    ISLAND_PLACEMENT: 1,
    MONSTER_PLACEMENT: 2,
    ACTIVE: 3,
    ENDED: 4,
} as const;

// ============================================================================
// Island Placement Constants
// ============================================================================

/**
 * Number of islands each player must place
 */
export const ISLANDS_PER_PLAYER = 5;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get monster definition by ID
 * @param id - Monster type ID (1-10)
 * @returns Monster definition or null if invalid
 */
export const getMonsterById = (id: number): MonsterDefinition | null => {
    if (id < 1 || id > 10) {
        return null;
    }
    return MONSTER_DEFINITIONS[id as MonsterTypeId] || null;
};

/**
 * Get monster name and URL by ID (legacy compatibility)
 * Uses monsterList for url so asset filenames (e.g. loch_ness_side.png) match.
 * @param id - Monster type ID (1-10)
 * @returns Object with id, name, and url, or null if invalid
 */
export const getMonsterNames = (id: number): { id: number; name: string; url: string } | null => {
    const monster = getMonsterById(id);
    if (!monster) {
        console.warn(`Could not find monster with id: ${id}`);
        return null;
    }
    const listEntry = monsterList.find((m) => m.id === id);
    const url = listEntry?.url ?? monster.name.toLowerCase().replace(/\s+/g, '_');
    return {
        id: monster.id,
        name: monster.name,
        url,
    };
};

/**
 * Get all monster definitions as an array
 * @returns Array of all monster definitions
 */
export const getAllMonsters = (): MonsterDefinition[] => {
    return Object.values(MONSTER_DEFINITIONS);
};
